import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import './subjectnotes.css';

const Icons = {
    Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Star: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16.5 13.5 18.8 21 12 16.8 5.2 21 7.5 13.5 2 9 9 9 12 2"></polygon></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Trash: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Calendar: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    Clock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
    Note: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="M8 16h5"></path></svg>
};

export default function SubjectNotes() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [subject, setSubject] = useState({ name: '', grade: '', class_name: '' });
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState('');
    const [isImportant, setIsImportant] = useState(false);
    const [dueDate, setDueDate] = useState('');

    const loadNotes = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/subject_notes.php`, { params: { subject_id: id } });
            setNotes(response.data.notes || []);
        } catch (error) {
            setNotes([]);
        }
    }, [id]);

    useEffect(() => {
        const loadSubject = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`);
                setSubject({
                    name: response.data.subject_name || 'Subject Notes',
                    grade: response.data.grade || '',
                    class_name: response.data.class_name || ''
                });
            } catch (error) {
                setSubject({ name: 'Subject Notes', grade: '', class_name: '' });
            } finally {
                setLoading(false);
            }
        };

        loadSubject();
        loadNotes();
    }, [id, loadNotes]);

    // Listen for cross-page notes updates (quick sync without global state)
    useEffect(() => {
        const onNotesUpdated = (e) => {
            try {
                if (!e?.detail || !e.detail.subject_id) return;
                if (String(e.detail.subject_id) !== String(id)) return;
            } catch (err) {}
            loadNotes();
        };
        window.addEventListener('notesUpdated', onNotesUpdated);
        return () => window.removeEventListener('notesUpdated', onNotesUpdated);
    }, [id, loadNotes]);

    const handleCreateNote = async (e) => {
        e.preventDefault();
        const cleanText = noteText.trim();
        if (!cleanText) return;

        setSaving(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'create',
                subject_id: id,
                note_text: cleanText,
                is_important: isImportant ? 1 : 0,
                due_date: dueDate || null
            });

            if (!res.data.success) {
                alert(res.data.error || 'Failed to save note');
            } else {
                setNoteText('');
                setIsImportant(false);
                setDueDate('');
                await loadNotes();
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const updateNote = async (noteId, patch) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'update',
                note_id: noteId,
                ...patch
            });
            if (!res.data.success) {
                alert(res.data.error || 'Failed to update note');
                return;
            }
            await loadNotes();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to update note');
        }
    };

    const deleteNote = async (noteId) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'delete',
                note_id: noteId
            });
            if (!res.data.success) {
                alert(res.data.error || 'Failed to delete note');
                return;
            }
            await loadNotes();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete note');
        }
    };

    const getDueDateStatus = (dueDate) => {
        if (!dueDate) return { status: 'none', label: '' };
        
        const now = new Date();
        const due = new Date(dueDate);
        const diffMs = due - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffMs < 0) {
            return { status: 'overdue', label: 'Overdue', daysLeft: diffDays };
        } else if (diffDays === 0) {
            return { status: 'due-today', label: 'Due Today', daysLeft: 0 };
        } else if (diffDays <= 3) {
            return { status: 'due-soon', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`, daysLeft: diffDays };
        } else if (diffDays <= 7) {
            return { status: 'due-week', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`, daysLeft: diffDays };
        }
        return { status: 'future', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`, daysLeft: diffDays };
    };

    return (
        <div className="subject-notes-page">
            <div className="notes-header">
                <div>
                    <p className="notes-kicker">Subject notes</p>
                    <h1>{loading ? 'Loading...' : subject.name}</h1>
                    <p className="notes-subtitle">Type a note to create a card. Mark it important or done when needed.</p>
                </div>
                <Link to={`/subjects/${id}`} className="back-link">Back to subject</Link>
            </div>

            <form className="notes-card notes-composer" onSubmit={handleCreateNote}>
                <div className="notes-meta">
                    <span>{subject.grade ? `Grade ${subject.grade}` : 'Grade not set'}</span>
                    <span>{subject.class_name || 'No class name'}</span>
                    <span>{notes.length} notes</span>
                </div>

                <label className="notes-label" htmlFor="subject-notes">Add a note</label>
                <textarea
                    id="subject-notes"
                    className="notes-textarea"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Example: Test on Friday, bring calculators, parent meeting next week..."
                    rows={5}
                />

                <div className="notes-date-row">
                    <label htmlFor="note-due-date" className="note-date-label">
                        <Icons.Calendar /> Due Date (optional)
                    </label>
                    <input
                        id="note-due-date"
                        type="datetime-local"
                        className="note-date-input"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                    />
                </div>

                <div className="notes-compose-actions">
                    <label className="important-toggle">
                        <input type="checkbox" checked={isImportant} onChange={(e) => setIsImportant(e.target.checked)} />
                        <span>Important</span>
                    </label>
                    <button className="notes-save-btn" type="submit" disabled={saving || loading || noteText.trim() === ''}>
                        {saving ? 'Saving...' : <><Icons.Plus /> Add note</>}
                    </button>
                </div>

                {notes.length > 0 && (
                    <div className="notes-list">
                        {notes.map((note) => (
                            <article key={note.id} className={`note-card ${note.is_important ? 'important' : ''} ${note.is_done ? 'done' : ''} ${note.due_date ? `due-${getDueDateStatus(note.due_date).status}` : ''}`}>
                                <div className="note-card-top">
                                    <div className="note-card-meta">
                                        <span className="note-date">{new Date(note.created_at).toLocaleString()}</span>
                                        {note.is_important ? <span className="note-pill important-pill"><Icons.Star /> Important</span> : null}
                                        {note.is_done ? <span className="note-pill done-pill"><Icons.Check /> Done</span> : null}
                                        {note.due_date && getDueDateStatus(note.due_date).status !== 'none' && (
                                            <span className={`note-pill due-pill due-${getDueDateStatus(note.due_date).status}`}>
                                                <Icons.Clock /> {getDueDateStatus(note.due_date).label}
                                            </span>
                                        )}
                                    </div>
                                    <div className="note-card-actions">
                                        <button type="button" className={`note-action-btn ${note.is_important ? 'active' : ''}`} onClick={() => updateNote(note.id, { is_important: note.is_important ? 0 : 1 })} title="Mark important">
                                            <Icons.Star />
                                        </button>
                                        <button type="button" className={`note-action-btn ${note.is_done ? 'active' : ''}`} onClick={() => updateNote(note.id, { is_done: note.is_done ? 0 : 1 })} title="Mark done">
                                            <Icons.Check />
                                        </button>
                                        <button type="button" className="note-action-btn danger" onClick={() => deleteNote(note.id)} title="Delete note">
                                            <Icons.Trash />
                                        </button>
                                    </div>
                                </div>
                                <p className="note-text">{note.note_text}</p>
                            </article>
                        ))}
                    </div>
                )}
            </form>

            {notes.length === 0 && !loading && (
                <div className="notes-empty-state">
                    <Icons.Note />
                    <h3>No notes yet</h3>
                    <p>Add a reminder, exam date, or anything you want to track for this subject.</p>
                </div>
            )}
        </div>
    );
}